import { isAuthRequiredError } from './authError';

/**
 * How many times a query is retried after its first failure. Unchanged from the `retry: 1`
 * this policy replaces — the change here is *which* failures are eligible, not how many
 * attempts a retryable one gets.
 */
export const MAX_QUERY_RETRIES = 1;

/**
 * react-query's retry policy for this app: **never retry a request that was never sent.**
 *
 * 🔴 **Why this is not a micro-optimisation.** `AuthRequiredError` means the API layer could
 * not obtain a token and therefore issued no request (`lib/authError.ts`). Retrying asks MSAL
 * to try again — and MSAL's silent renewal runs in a hidden iframe with its own timeout, so a
 * second attempt costs the user another full `monitor_window_timeout` while the outcome is
 * already determined: no token is going to appear without user interaction.
 *
 * Under the previous blanket `retry: 1`, a signed-out user paid that timeout **twice** before
 * the UI was permitted to say anything, and BL-147's console shows exactly that — the CSP block
 * on the renewal iframe repeated, then `BrowserAuthError: monitor_window_timeout`. Fixing the
 * CSP restores the happy path; this is what stops the unhappy path from being a long silence.
 *
 * ⚠ **The auth check must be able to WIN, which is not the same as running first — and the
 * difference was measured, not assumed.** A mutant that merely swapped the two conditions
 * survived the whole suite, correctly: they are independent, so the order between them changes
 * nothing. What the tests do kill is a budget-consulted-first shape that RETURNS EARLY
 * (`if (failureCount < MAX) return true;`), which retries an auth failure once and refuses only
 * the second attempt — the bug wearing the fix's name. 6 of 11 tests fell to that one.
 *
 * 🔬 `failureCount` is 0 on the first failure: query-core evaluates `retry(failureCount, error)`
 * and only increments afterwards, so `< MAX_QUERY_RETRIES` is the exact equivalent of the
 * numeric `retry: 1` form. Verified against `@tanstack/query-core`'s retryer, not assumed.
 */
export function retryUnlessAuthRequired(failureCount: number, error: unknown): boolean {
  if (isAuthRequiredError(error)) return false;
  return failureCount < MAX_QUERY_RETRIES;
}
